<script setup>
  import { computed, onMounted, ref, onBeforeUnmount, toRaw } from 'vue';
  import ButtonQuantity from '../ui/ButtonQuantity.vue';
  import CartInformation from './CartInformation.vue';
  import InputQuantity from '../ui/InputQuantity.vue';
  import CartIsbn from './CartIsbn.vue';
  import { useOfferStore } from '../../stores/OfferStore';
  import CartOfferRelated from './CartOfferRelated.vue';
  import Modal from '../ui/Modal.vue';
  import { useI18n } from 'vue-i18n';
  import { formatDate, showPriceWithCurrency  } from '../../utils/format_offer.js';
  import { useOrderStore } from '../../stores/OrderStore.js';
  import CartItemOrderDetail from './CartItemOrderDetail.vue';
  import Loader from '../ui/Loader.vue';
  import { useOfferLicensesStore } from '../../stores/OfferLicensesStore.js';
  import { marked } from 'marked';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import imagePlaceHolder from '../ui/assets/placeholder-image-vertical.png';

  const { t } = useI18n();
  const offerStore = useOfferStore();
  const props = defineProps({
    offer: {
      type: Object,
      required: true
    },
    cart_editable: {
      type: Boolean,
      required: false,
      default: false
    },
    display_cart_item_orders: {
      type: Boolean,
      required: true,
      default: false
    },
    display_offer_details: {
      type: Boolean,
      required: false,
      default: false
    },
    index: {
      type: Number,
      required: true
    }
  });

  const offersStore = useOfferStore();
  const cartStore = useBrowseCartStore();
  const orderStore = useOrderStore();
  const licensesStore = useOfferLicensesStore();

  const showModal = ref(false);
  const showModalValidation = ref({ status: false, visible: false });
  const showModalAlreadyPurchased = ref({ visible: false, loading: false });
  const showModalPurchaseStatus = ref(false);
  const initialModalLicensesAttributes = toRaw({ show: false, loading: false, icon_arrow: 'bi-caret-down-fill', close: false });
  const modalLicenses = ref(initialModalLicensesAttributes);

  const refundMode = computed(() => {
    return cartStore.refund_mode;
  })

  const cart = computed(() => {
    return cartStore.cart;
  });

  const incrementQuantity = (offer_id) => {
    offersStore.incrementQuantity(offer_id);
  };

  const decrementQuantity = (offer_id) => {
    offersStore.decrementQuantity(offer_id);
  };

  const total_price = computed(() => {
    return props.offer.price.price_without_taxes * props.offer.offer.quantity;
  });

  const currency =  props.offer.price?.currency?.toUpperCase();

  const published_at = computed(() => {
    const date = new Date(props.offer.publication.published_at);
    return formatDate(date)
  });

  const authors =  computed(() => {
    if (!props.offer.publication.authors) return '';
    return props.offer.publication.authors.map((author) => {
      return author;
    }).join(', ');
  });

  const category = computed(() => {
    const categories = props.offer.publication.categories || [];
    const categoriesArray = Array.isArray(categories) ? categories : [];

    if (categoriesArray.length === 0) return '-';

    // The first category in the array is the root/top-most category
    const rootCategory = categoriesArray[0];

    if (rootCategory === 'FBFIC000000') return t('cart.category.fiction');
    if (rootCategory === 'FBNFC000000') return t('cart.category.non_fiction');
    return '-';
  });

  const audience = computed(() => {
    const age = props.offer.publication.age;
    if (!age) return '-';

    const classifications = props.offer.publication.classifications || {};

    // Find the key where the code matches the age
    const classificationKey = Object.keys(classifications).find(
      key => classifications[key] === age
    );

    if (classificationKey) {
      return t(`cart.audience.${classificationKey}`);
    }
    return age;
  });

  const resetModalLicenses = () => {
    modalLicenses.value = initialModalLicensesAttributes;
  }

  const publicationPath = computed(() => {
    return props.offer.publication.path
  })

  const classDisableQuantity = computed(() => {
    if (offersStore.loading_quantity) {
      return 'quantity-disabled';
    }
    return ''
  });

  const itemStatusByCustomerService = computed(() => {
    return (cartStore.cart.policy.customer_service && !props.offer.offer.from_de_marque);
  });

  const isNumilogAudiobook = computed(() => {
    return props.offer.offer.sub_provider === 'numilog' && props.offer.offer.format === 'audiobook';
  });

  const displayQuantity = computed(() => {
    return (cartStore.cart.policy.cart_editable && props.offer.offer.available && cartStore.cart.status === 'new' && !itemStatusByCustomerService.value);
  });

  const cartItemStyle = computed(() => {
    if (props.offer.offer.refunded) {
      return 'cart-item cart-grid cart-item-refunded'
    } else if ((!props.offer.offer.available || itemStatusByCustomerService.value) && !cartStore.cart.automatic_offer_replacement_and_removal) {
      return 'cart-item cart-grid cart-item-warning'
    } else if ((!props.offer.original_offer || itemStatusByCustomerService.value) && cartStore.cart.automatic_offer_replacement_and_removal) {
      return 'cart-item cart-grid cart-item-warning'
    } else if (props.offer.only_offer_deleted) {
      // hide deleted offers
      return 'cart-item cart-grid hidden'
    }
    return 'cart-item cart-grid'
  })

  const showCheckBox = computed(() => {
    const conditional = (refundMode.value && props.offer.offer.from_de_marque && props.offer.status === 'validate' && !props.offer.offer.refunded)

    return conditional || (cartStore.cart.policy.cart_editable && !refundMode.value)
  });

  const visibleCheck = computed(() => {
    return showCheckBox.value && !(props.offer.status === 'validate' && !refundMode.value);
  });

  const openModalAlreadyPurchased = async () => {
    showModalAlreadyPurchased.value = { loading: true, visible: true };
    offersStore.offer_purchases = null
    await offersStore.fetchOfferAlreadyPurchased(cart.value.id, props.offer.offer.id)
    showModalAlreadyPurchased.value.loading = false
  }

  const fetchOrders = async () => {
    await orderStore.fetchOrder(cart.value.id, props.offer.offer.id);
  }

  const openModalValidation = (status) => {
    showModalValidation.value = { status: status, visible: true };
    fetchOrders();
  }

  const otherLincenses = (cartItemId) =>{
    const item = licensesStore.offerLicenses.find(i => i.id === cartItemId)
    return item ? item.data : []
  };

  const offersSelectedReplaced = computed(() => {
    return offerStore.offersSelectedReplaced
  });

  const classValidateChanges = computed(() => {
    return {
      'button button--primary cart-button-validate_item hidden': offersSelectedReplaced.value.length === 0,
      'button button--primary cart-button-validate_item': offersSelectedReplaced.value.length > 0
    }
  });

  const fetchOfferLicenses = async (item) => {
    if (modalLicenses.value.icon_arrow === 'bi-caret-up-fill') {
      return resetModalLicenses();
    }

    modalLicenses.value = { show: true, loading: true, icon_arrow: 'bi-caret-up-fill' };
    if(otherLincenses(item.id).length == 0) {
      await licensesStore.fetchLicenses(cart.value.id, item.offer.id, item.id);
    }
    modalLicenses.value.loading = false;
  }

  const fetchSwitchLicence = async (offerId) => {
    modalLicenses.value.loading = true;
    const anotherItemOffer = offersStore.offers.find(item => item.offer.id === offerId);
    if (anotherItemOffer) {
      props.offer.quantity = Number(props.offer.offer.quantity) + Number(anotherItemOffer.offer.quantity);
    }
    await licensesStore.switchLicense(cart.value.id, props.offer, offerId);
    await offersStore.fetchOffers(cart.value.id, [offerId], true);
    if(anotherItemOffer) { offersStore.removeFromList(anotherItemOffer.id); }
    resetModalLicenses();
  }

  const handleClickOutside = (event) => {
    const licensesLine = event.target.closest('.licenses-line');
    if (!licensesLine && modalLicenses.value.show && !event.target.closest('.close-button')) {
      resetModalLicenses();
    }
    else if (licensesLine) {
      const id = licensesLine.getAttribute('data-id');
      if(id != props.offer.offer.id) {
        resetModalLicenses();
      }
    }
  }

  const markAsOriginalOffer = () => {
    offersStore.markAsOriginalOffer(props.offer);
  }

  const imagePlaceHolderUrl = (event) => {
    event.target.src = imagePlaceHolder;
  }

  onMounted(() => {
    if (!props.display_cart_item_orders) {
      const cartItemIcons = document.querySelectorAll('.cart-item-icons');
      cartItemIcons.forEach((cartItemIcon) => {
        cartItemIcon.classList.add('cart-item-icons--readonly');
      });
    }
    document.addEventListener('click', handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
  });
</script>

<template>
  <li :class="cartItemStyle" :style="{ zIndex: modalLicenses.show ? 10 : 'auto', position: modalLicenses.show ? 'relative' : '' }" :data-cart-item-status="offer.status" role="group">
    <div class="cart-grid__column cart-grid__checkbox-content">
      <div class="form-check ms-3" v-if="visibleCheck">
        <input class="form-check-input fs-6" type="checkbox" :value="offer.id" id="checkDefault" v-model="offer.checked" :aria-labelledby="`cart-item-title-${offer.id}`">
      </div>
    </div>
    <div :class="['cart-grid__column', 'cart-item-book', { 'cart-item-book--dropdown-open': modalLicenses.show }]">
      <div class="cart-item-book__cover">
        <div class="cover-tags" v-if="!offer.original_offer">
          <span class="in-sale-tag">
            {{ t('cart.replaced') }}
          </span>
        </div>
        <img :src="offer.publication.cover" class="cart-item-book__cover-img" @error="imagePlaceHolderUrl">
      </div>
      <div class="cart-item-book-details">
        <div class="refund-item-reason" v-if="offer.offer.refunded && offer.refund_details">
          {{ t('cart.refund.reason.title') }}: {{ t(`cart.refund.reason.${offer.refund_details.reason}`) }}
        </div>
        <div>
          <h2 class="fs-6">
            <a class="cart-item-title" :href="publicationPath" :id="`cart-item-title-${offer.id}`">
              <i class="from-quebec" v-if="offer.from_quebec"></i> {{ offer.publication.title }}
            </a>
          </h2>
          <div class="cart-item-authors">{{ authors }}</div>
          <CartIsbn :item="offer" :key="offer.offer.id"/>
          <div class="cart-item-details">
            <CartInformation :item="offer.offer" :key="offer.offer.id" />
          </div>
        </div>
        <div class="box-parent" v-if="cart.status === 'new'">
          <div class="licenses-line" :style="{ zIndex: 1 }" :data-id="offer.offer.id">
            <div :class="{ 'modal-license': true, active: modalLicenses.show }">
              <button @click="fetchOfferLicenses(offer)" class="btn btn-sm btn-outline-secondary ms-0" :aria-expanded="modalLicenses.show" aria-controls="offer_licenses">
                <span class="me-1">{{ t('cart.license.see_other_available_license_types_for_this_publication') }}</span>
                <i :class="`bi ${modalLicenses.icon_arrow}`" aria-hidden="true"></i>
              </button>
              <div class="offer_licenses" v-if="modalLicenses.show">
                <Loader v-if="modalLicenses.loading"/>
                <template v-if="!modalLicenses.loading">
                  <div class="text-switch" v-if="otherLincenses(offer.id).length">
                    <span v-html="marked(t('cart.license.select_the_desired_license_type_and_click_the_replace_button'))"></span>
                    <span class="btn btn-sm btn-primary d-inline-block" style="--bs-btn-padding-y: .1rem; --bs-btn-padding-x: .3rem; --bs-btn-font-size: .75rem;"><i class="bi bi-arrow-down-up"></i></span>
                  </div>
                  <span class="text-switch" v-else v-html="marked(t('cart.license.no_other_license_type_is_available_for_this_publication'))"></span>
                </template>
                <ul v-if="!modalLicenses.loading && otherLincenses(offer.id).length">
                  <li v-for="item in otherLincenses(offer.id)" :key="item.id" :class="{ selected: (item.id === offer.offer.id) }">
                    <CartInformation :item="item.offer_details" :offerPrice="item.offer_price" @switchLicence="(offerId) => fetchSwitchLicence(offerId)" :enabled="item.id != offer.offer.id" />
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <a
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.validate_items')"
        v-on="clickAction"
        :class="classValidateChanges"
        v-if="!offer.original_offer"
        @click.prevent="markAsOriginalOffer()"
      >
        {{ t('cart.validate_changes') }}
      </a>
    </div>
    <div class="cart-grid__column cart-item-release-date cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.release_date') }}</span>
      {{ published_at }}
    </div>
    <div class="cart-grid__column cart-item-category cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.category') }}</span>
      {{ category }}
    </div>
    <div class="cart-grid__column cart-item-audience cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.audience') }}</span>
      {{ audience }}
    </div>
    <div class="cart-grid__column cart-item-publisher cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.publisher') }}</span>
      <a
        href="#"
        v-if="offer.publication.publisher"
        data-bs-toggle="tooltip" :data-bs-title="t('cart.header.filter.filter_by_publisher')"
        @click.prevent="offersStore.setFilter('publisher', offer.publication.publisher)">
        {{ offer.publication.publisher.name }}
      </a>
      <template v-else>-</template>
    </div>
    <!-- FBKS-3907: hidden for now
    <div class="cart-grid__column cart-item-online-reader cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.online_reading_listening') }}</span>
      <i :class="offer.offer.online_reading_listening_compatible ? 'bi bi-check-circle-fill cart-icon_green' : 'bi bi-slash-circle-fill cart-icon_red'"></i>
      <i v-if="isNumilogAudiobook" class="bi bi-headphones cart-icon_orange cart-tooltip" :data-tooltip="t('cart.item_status.numilog_online_reader')"></i>
    </div>
    -->
    <div class="cart-grid__column cart-item-state cart-item--align-center">
      <span class="sr-only">{{ t('cart.header.labels.status') }}</span>
      <div class="cart-item-states-row">
        <i class="bi bi-check-lg cart-icon_gray button-icon cart-tooltip" @click.prevent="openModalAlreadyPurchased" :data-tooltip="t('cart.item_status.already_purchased')" v-if="offer.publication && offer.publication.already_purchased"></i>
        <Modal :show="showModalAlreadyPurchased.visible" @close="showModalAlreadyPurchased.visible = false">
          <Loader v-if="showModalAlreadyPurchased.loading" />
          <template v-slot:title>
            <div v-if="offersStore.offer_purchases">
              {{ offersStore.offer_purchases.title }}
            </div>
          </template>
          <table class="table" v-if="offersStore.offer_purchases" :aria-label="offersStore.offer_purchases.title">
            <thead>
              <tr>
                <th scope="col">{{ offersStore.offer_purchases.table.title }}</th>
                <th scope="col">{{ offersStore.offer_purchases.table.username }}</th>
                <th scope="col">{{ offersStore.offer_purchases.table.quantity }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="purchase in offersStore.offer_purchases.purchases" :key="purchase.id">
                <td>
                  <a v-if="purchase.id" :href="purchase.url">{{ purchase.title }}</a>
                  <span v-else>{{ purchase.title }}</span>
                </td>
                <td>{{ purchase.username }}</td>
                <td>{{ purchase.quantity }}</td>
              </tr>
            </tbody>
          </table>
        </Modal>

        <i class="bi bi-cart-fill cart-icon_gray button-icon cart-tooltip" @click.prevent="showModal = true;" v-if="offer.publication.in_another_cart" :data-tooltip="t('cart.item_status.in_another_cart')"></i>
        <Modal :show="showModal" @close="showModal = false">
          <template v-slot:title>
            {{ t('cart.status.in_another_cart') }}
          </template>
          <CartOfferRelated :offer_id="offer.offer.id" :cart_id="offer.cart_id" />
        </Modal>

        <i class="bi bi-clock-history cart-icon_gray button-icon cart-tooltip" @click.prevent="showModalPurchaseStatus = true;" v-if="offer.already_purchased" :data-tooltip="t('cart.item_status.purchase_status')"></i>
        <Modal :show="showModalPurchaseStatus" @close="showModalPurchaseStatus = false">
          <template v-slot:title>
            {{ t('cart.purchase_status.title') }}
          </template>
          <div>
            {{ t('cart.purchase_status.first_purchase_date') }}: <strong>{{ offer.already_purchased.active_copies.first_purchase_date }}</strong>
          </div>
          <ul>
            <li v-if="offer.already_purchased.active_copies.total > 0">
              <div>
                {{ offer.already_purchased.active_copies.total == 1 ? t('cart.purchase_status.active_copy.one') : t('cart.purchase_status.active_copy.other', { count: offer.already_purchased.active_copies.total }) }}
              </div>
              <ul>
                <li v-if="offer.already_purchased.active_copies.unlimited_loans">{{ t('cart.purchase_status.unlimited_loans') }}</li>
                <li v-else>{{ t('cart.purchase_status.remaining_loans', { count: offer.already_purchased.active_copies.loans }) }}</li>

                <li v-if="offer.already_purchased.active_copies.no_expiration_date">{{ t('cart.purchase_status.unlimited_lifetime') }}</li>
                <li v-else>{{ t('cart.purchase_status.last_copy_expiration_date', { date: offer.already_purchased.active_copies.max_expiration_date }) }}</li>
              </ul>
            </li>
            <li v-if="offer.already_purchased.inactive_copies.total > 0">
              <div>
                {{ offer.already_purchased.inactive_copies.total == 1 ? t('cart.purchase_status.inactive_copy.one') : t('cart.purchase_status.inactive_copy.other', { count: offer.already_purchased.inactive_copies.total }) }}
              </div>
              <ul>
                <li>
                  {{ t('cart.purchase_status.inactive_date', { date: offer.already_purchased.inactive_copies.became_inactive_at }) }}
                </li>
              </ul>
            </li>
          </ul>
        </Modal>

        <i v-if="offer.api_created" class="bi bi-robot cart-tooltip cart-icon_gray" :data-tooltip="t('cart.api_created')"></i>
      </div>
    </div>
    <div class="cart-grid__column cart-item-unit-price cart-item--align-right">
      <span class="sr-only">{{ t('cart.header.labels.price') }}</span>
      {{ showPriceWithCurrency(currency, offer.price.price_without_taxes) }}
    </div>
    <div class="cart-grid__column cart-item__quantity-box" :class="{ 'quantity-disabled': classDisableQuantity.includes('quantity-disabled') }">
      <span class="sr-only">{{ t('cart.header.labels.quantity') }}</span>
      <template v-if="displayQuantity">
        <div class="btn-group btn-group-sm" role="group">
          <ButtonQuantity button_type="decrease" :quantity="offer.offer.quantity" @click.prevent="decrementQuantity(offer.id)"/>
          <InputQuantity :offer_id="offer.id" :quantity="offer.offer.quantity" />
          <ButtonQuantity button_type="increase" :quantity="offer.offer.quantity" @click.prevent="incrementQuantity(offer.id)" />
        </div>
      </template>
      <template v-else>
        <span class="cart-item__quantity">{{ offer.offer.quantity }}</span>
      </template>
    </div>
    <div class="cart-grid__column cart-item-total-price">
      <span class="sr-only">{{ t('cart.header.labels.total_price') }}</span>
      <div class="price">
        {{ showPriceWithCurrency(currency, total_price) }}
      </div>
      <div class="refunded" v-if="offer.offer.refunded && !offer.refund_details">
        {{ t('cart.item_status.refunded') }}
      </div>
    </div>
    <template v-if="offer.offer.available">
      <i data-bs-toggle="tooltip" :data-bs-title="t('cart.item_status.failed')" :aria-label="t('cart.item_status.failed')" class="bi bi-exclamation-circle cart-item-icons cart-item-icons--error" @click.prevent="openModalValidation('failed')"></i>
      <i data-bs-toggle="tooltip" :data-bs-title="t('cart.item_status.validated')" :aria-label="t('cart.item_status.validated')" class="bi bi-check-lg cart-item-icons cart-item-icons--validated" @click.prevent="openModalValidation('validate')"></i>
      <span data-bs-toggle="tooltip" :data-bs-title="t('cart.item_status.refunded')" class="cart-item-icons cart-item-icons--refunded">{{ t('cart.item_status.refunded') }}</span>

      <Modal :show="showModalValidation.visible" @close="showModalValidation.visible = false" size="large">
        <template v-slot:title>
          {{ t('offers.cart_orders.title') }}
        </template>
        <Loader v-if="orderStore.loading" />
        <CartItemOrderDetail :data="orderStore.data" :status="showModalValidation.status" v-else />
      </Modal>
    </template>
  </li>
</template>

<style scoped>
  .button-icon {
    cursor: pointer;
  }
  .cart-item-states {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
  .cart-item-states-row {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .cart-item-states-separator {
    width: 100%;
    margin: 0;
    border: 0;
    border-top: 1px solid #ddd;
  }
  .cart-icon_green {
    color: #28a745;
  }
  .cart-icon_red {
    color: #dc3545;
  }
  .cart-icon_orange {
    color: #e67700 !important;
  }
  .cart-tooltip {
    position: relative;
    cursor: pointer;
    display: inline-block;
    font-size: 1rem;
    font-style: normal !important;
    line-height: 1;
    color: #000;
  }
  .cart-tooltip::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 0.25rem 0.5rem;
    background-color: rgba(0, 0, 0, 0.9);
    color: white;
    font-size: 0.75rem;
    white-space: nowrap;
    border-radius: 0.25rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s;
    margin-bottom: 0.5rem;
    z-index: 1000;
  }
  .cart-tooltip:hover::after {
    opacity: 1;
  }
  .cart-item-book__cover-img {
    max-width: 100px !important;
  }
  .cart-item-book__cover {
    max-width: 120px;
  }
  .quantity-disabled {
    opacity: 0.3;
    pointer-events: none;
  }
  .cart-item-warning {
    background-color: #f6dade !important;
  }
  .refunded_reason {
    background-color: #d3455c;
  }
  .cart-item-details {
    position: relative;
  }
  .licenses-line {
    display: inline-flex;
    position: relative;
  }
  .licenses-line .modal-license {
    padding: 3px 5px;
    position: relative;
    min-width: 700px
  }
  .licenses-line .active {
    background-color: #fff;
    border: 3px solid var(--primary);
    border-radius: 5px;
  }
  .licenses-line .active .offer_licenses {
    position: static;
    border: none;
    box-shadow: none;
    border-radius: 0;
    min-width: auto;
  }
  .licenses-line .modal-license a {
    display: block;
    cursor: pointer;
    text-decoration: none;
    font-size: 1em;
    position: relative;
    color: var(--dark-gray-2);
  }
  .licenses-line .modal-license a:hover {
    color: var(--primary);
    text-decoration: underline;
  }
  .licenses-line .active a {
    color: var(--primary);
  }
  .licenses-line .modal-license a span {
    display: inline-block;
  }
  :deep() .licenses-line .modal-license p {
    margin: 0 !important;
  }
  .licenses-line .modal-license a .arrow {
    line-height: 0;
    top: 13px;
    position: absolute;
  }
  .licenses-line .active a .arrow {
    line-height: 0;
    top: 11px;
    position: absolute;
  }
  .licenses-line .offer_licenses {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 100;
    background-color: #fff;
    min-width: 700px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #ccc;
    border-top: none;
    border-radius: 0 0 5px 5px;
  }
  .text-switch span {
    display: inline-block;
    margin-right: 5px;
  }
  .licenses-line .offer_licenses .loader_box {
    padding: 50px;
    overflow-y: auto;
  }
  .licenses-line .offer_licenses ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .licenses-line .offer_licenses li {
    padding: 1px 7px;
  }
  .licenses-line .offer_licenses .text-switch {
    padding: 10px 7px;
    display: block;
  }
  .licenses-line .offer_licenses li.selected {
    background-color: var(--light-gray);
  }
  :deep() .licenses-line .offer_licenses li.selected .switch {
    pointer-events: none;
  }
  .switch {
    color: #fff;
    border-radius: 5px;
    padding: 1px;
    display: inline;
    justify-self: right;
    font-size: 1em;
    background-color: var(--primary);
  }
  .box-parent {
    position: relative;
    min-height: 30px;
    width: 100%;
  }
  :deep() .cart-item-format {
    position: relative;
    top: -1px;
  }
  .refunded {
    color: #d3455c;
    background-color: #ffe5ea;
    display: inline-block;
    padding: 0.2rem 0.6rem;
    border-radius: 0.3rem;
    margin: 10px 0;
  }
  .cart-item-refunded {
    background-color: #f6dade !important;
    border: 2px solid #d3455c;
    margin-bottom: 5px;
  }
  .refund-item-reason {
    position: absolute;
    right: 20px;
    background-color: #d3455c;
    color: #fff;
    border-radius: 10px;
    padding: 5px 10px;
    font-size: 0.8em;
  }
  .cart-item-book {
    position: relative;
  }
  .cart-item-book--dropdown-open {
    overflow: visible !important;
  }
  .from-quebec:before {
    width: 18px;
    height: 18px;
    margin-right: 5px;
    position: relative;
    top: 3px;
  }
  .cart-item-title {
    display: inline-block;
    text-wrap: wrap;
  }
  .cart-item--align-left {
    text-align: left;
    align-self: center;
  }
  .cart-item--align-right {
    text-align: right;
    align-self: center;
  }
  .cart-item--align-center {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .cart-item-release-date {
    grid-area: release_date;
  }
  .cart-item-category {
    grid-area: category;
  }
  .cart-item-audience {
    grid-area: audience;
  }
  .cart-item-publisher {
    grid-area: publisher;
    text-align: center;
    overflow-wrap: anywhere;
  }
  /* FBKS-3907: hidden for now
  .cart-item-online-reader {
    grid-area: online_reader;
    gap: 4px;
  }
  */
  .cart-item-state {
    grid-area: states;
  }
  .cart-item-unit-price {
    grid-area: price;
  }
  .cart-item__quantity-box {
    grid-area: quantity;
  }
  .cart-item-total-price {
    grid-area: total_price;
    text-align: right;
    align-self: center;
  }
</style>
